import React from 'react';
import { Filter, ChevronDown, ChevronUp, X } from 'lucide-react';

const HospitalFilters = ({
  filters,
  onFilterChange,
  showAdvancedFilters,
  onToggleAdvancedFilters,
  onClearFilters
}) => {
  
  const serviceOptions = [
    'Emergency Care', 'Cardiology', 'Neurology', 'Orthopedics', 'Pediatrics',
    'Oncology', 'Radiology', 'Laboratory', 'Surgery', 'Maternity', 'ICU',
    'Pharmacy', 'Ambulance', 'Blood Bank', 'Dialysis', 'Dermatology',
    'Psychiatry', 'Ophthalmology', 'ENT', 'Urology', 'Gastroenterology'
  ];

  const insuranceOptions = [
    'NHIF', 'Jubilee Insurance', 'CIC Insurance', 'APA Insurance', 
    'Madison Insurance', 'Britam', 'AAR Insurance', 'Resolution Insurance',
    'ICEA LION', 'Heritage Insurance', 'GA Insurance', 'Sanlam'
  ];

  const handleServiceToggle = (service) => {
    const currentServices = filters.services || [];
    const newServices = currentServices.includes(service)
      ? currentServices.filter(s => s !== service)
      : [...currentServices, service];
    
    onFilterChange('services', newServices);
  };

  const handleInsuranceToggle = (insurance) => {
    const currentInsurance = filters.insurance || [];
    const newInsurance = currentInsurance.includes(insurance)
      ? currentInsurance.filter(i => i !== insurance)
      : [...currentInsurance, insurance];
    
    onFilterChange('insurance', newInsurance);
  };

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Filter Header */}
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Hospitals</h3>
            
            {/* Active Filters Count */}
            {(() => {
              const activeCount = Object.values(filters).reduce((count, value) => {
                if (Array.isArray(value)) return count + value.length;
                if (value && value !== '' && value !== false) return count + 1;
                return count;
              }, 0);
              
              return activeCount > 0 && (
                <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                  {activeCount} active filter{activeCount !== 1 ? 's' : ''}
                </span>
              );
            })()}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
            >
              <X size={14} />
              Clear All
            </button>
            
            <button
              onClick={onToggleAdvancedFilters}
              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter size={16} />
              Advanced Filters
              {showAdvancedFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            onClick={() => onFilterChange('emergency', !filters.emergency)}
            className={`px-3 py-1 rounded-full text-sm transition-colors border ${
              filters.emergency
                ? 'bg-red-100 text-red-800 border-red-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            24/7 Emergency
          </button>
          
          <button
            onClick={() => onFilterChange('rating', filters.rating === '4' ? '' : '4')}
            className={`px-3 py-1 rounded-full text-sm transition-colors border ${
              filters.rating === '4'
                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            4+ Stars
          </button>
          
          <button
            onClick={() => {
              const hasICU = filters.services?.includes('ICU');
              const newServices = hasICU 
                ? filters.services.filter(s => s !== 'ICU')
                : [...(filters.services || []), 'ICU'];
              onFilterChange('services', newServices);
            }}
            className={`px-3 py-1 rounded-full text-sm transition-colors border ${
              filters.services?.includes('ICU')
                ? 'bg-blue-100 text-blue-800 border-blue-200'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
            }`}
          >
            ICU Available
          </button>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="px-4 sm:px-6 lg:px-8 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Location Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City or Location
              </label>
              <input
                type="text"
                value={filters.city || ''}
                onChange={(e) => onFilterChange('city', e.target.value)}
                placeholder="Enter city name..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Rating
              </label>
              <select
                value={filters.rating || ''}
                onChange={(e) => onFilterChange('rating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
                <option value="1">1+ Stars</option>
              </select>
            </div>

            {/* Search Radius */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Radius: {filters.radius || 25}km
              </label>
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={filters.radius || 25}
                onChange={(e) => onFilterChange('radius', parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5km</span>
                <span>100km</span>
              </div>
            </div>

            {/* Bed Capacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Beds
              </label>
              <select
                value={filters.minBeds || ''}
                onChange={(e) => onFilterChange('minBeds', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Any Size</option>
                <option value="50">50+ beds</option>
                <option value="100">100+ beds</option>
                <option value="200">200+ beds</option>
                <option value="500">500+ beds</option>
              </select>
            </div>
          </div>

          {/* Services Filter */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Medical Services
            </label>
            <div className="max-h-40 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {serviceOptions.map((service) => (
                  <label key={service} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.services?.includes(service) || false}
                      onChange={() => handleServiceToggle(service)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{service}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Insurance Filter */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Insurance Accepted
            </label>
            <div className="max-h-32 overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {insuranceOptions.map((insurance) => (
                  <label key={insurance} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.insurance?.includes(insurance) || false}
                      onChange={() => handleInsuranceToggle(insurance)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{insurance}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Filters */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasParking || false}
                onChange={(e) => onFilterChange('hasParking', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Parking Available</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.wheelchairAccessible || false}
                onChange={(e) => onFilterChange('wheelchairAccessible', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Wheelchair Accessible</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasWifi || false}
                onChange={(e) => onFilterChange('hasWifi', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Free WiFi</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={filters.hasPharmacy || false}
                onChange={(e) => onFilterChange('hasPharmacy', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-gray-700">On-site Pharmacy</span>
            </label>
          </div>

          {/* Apply/Reset Buttons */}
          <div className="mt-6 flex justify-end gap-2">
            <button
              onClick={onClearFilters}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reset Filters
            </button>
            <button
              onClick={onToggleAdvancedFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalFilters;